import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { getItemVersion } from "../../api/content/itemsApi";
import { useProjects } from "../../projects/projectContext";
import { useTabTitle } from "../../shell/workspace";
import { useLoad } from "../../shared/loading/useLoad";
import CopyReference from "../../ui/content/CopyReference";
import DiscussionPanel from "../discussion/DiscussionPanel";
import ItemLifecycleBadge from "./ItemLifecycleBadge";
import KindOfLine from "./KindOfLine";
import ArtifactDetails from "./artifact/ArtifactDetails";
import ProcessItemActions from "./ProcessItemActions";
import Chip from "../../ui/feedback/Chip";
import MarkdownRenderer from "../../ui/content/MarkdownRenderer";
import SourceView from "../../ui/content/source/SourceView";
import LoadStatus from "../../ui/feedback/LoadStatus";
import { itemKindText } from "../workflows/inputCards/typeWords";

/**
 * `/items/<item id>@<version>`: any project item at one exact version (NFR-16), with its
 * comments and issues (FR-VERSION-6). Reviews link here for connected items. Process items
 * offer Edit and Ask the agent; an artifact shows its versions, who or what made each, what
 * it was made from and what reads it (authoring-ui.md AU-5, AU-7).
 */
export default function ItemPage() {
  const { t } = useTranslation();
  const { itemReference = "" } = useParams();
  const projectId = useProjects().selectedProjectId;
  const load = useCallback((signal: AbortSignal) => getItemVersion(itemReference, signal), [itemReference]);
  const { state, reload } = useLoad(load);
  // A Workflow opened as an item is still a Workflow (IM-SHELL: "Workflow: …").
  useTabTitle(state.status === "loaded" ? { kind: state.value.kind === "workflow" ? "workflow" : "other", name: state.value.name } : null);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} loadingClassName="py-10" onRetry={reload} />;
  const item = state.value;
  return (
    <div className="mx-auto max-w-5xl space-y-4 py-4">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="app-title text-xl font-bold">{item.name}</h2>
          <Chip>{itemKindText(item.kind, t)}</Chip>
          <CopyReference value={item.reference} />
        </div>
        <KindOfLine definition={item.definition} />
        <ItemLifecycleBadge projectId={projectId} definition={item.definition} />
        {projectId === null ? null : <ProcessItemActions projectId={projectId} kind={item.kind} name={item.name} reference={item.reference} />}
      </header>
      {item.kind === "artifact" && projectId !== null ? (
        <>
          <ArtifactDetails projectId={projectId} itemId={item.itemId} reference={item.reference} />
          <details className="app-card p-3">
            <summary className="app-title cursor-pointer text-sm font-semibold">{t("authoring.artifactPage.storedFile")}</summary>
            {/* The light view: a closed disclosure still mounts its content, and the editor loads only when opened in full. */}
            <SourceView label={t("authoring.artifactPage.storedFile")} language="json" value={item.text} className="mt-2" />
          </details>
        </>
      ) : item.markdown !== null ? (
        // A prompt or a skill reads as the text it is written in; its stored definition on request.
        <>
          <MarkdownRenderer content={item.markdown} className="app-markdown app-card p-4 text-sm" />
          <details className="app-card p-3">
            <summary className="app-title cursor-pointer text-sm font-semibold">{t("workflows.view.definition")}</summary>
            <SourceView label={item.name} language="json" value={item.text} className="mt-2" />
          </details>
        </>
      ) : (
        <SourceView label={item.name} language={item.textIsTurtle ? "turtle" : "json"} value={item.text} presentation="full" />
      )}
      {projectId !== null ? (
        <DiscussionPanel
          projectId={projectId}
          subject={{ kind: "item", value: item.itemId }}
          frame={(panel) => (
            <section className="app-card space-y-2 p-3">
              <h3 className="app-title text-base font-semibold">{t("discussion.title")}</h3>
              {panel}
            </section>
          )}
        />
      ) : null}
    </div>
  );
}
